/**
 * WHIP (WebRTC-HTTP Ingestion Protocol) Client
 * 
 * Handles WebRTC connection establishment for host audio streaming
 * using the WHIP protocol to communicate with the backend.
 */

export interface WhipClientConfig {
    sessionId: string;
    apiUrl: string;
    authToken: string;
    enableAutoReconnect?: boolean;
    maxReconnectAttempts?: number;
}

export interface WhipConnectionState {
    state: RTCPeerConnectionState;
    iceConnectionState: RTCIceConnectionState;
    iceGatheringState: RTCIceGatheringState;
    reconnecting?: boolean;
    reconnectAttempt?: number;
}

export type WhipConnectionStateCallback = (state: WhipConnectionState) => void;
export type WhipErrorCallback = (error: Error) => void;

export class WhipClient {
    private peerConnection: RTCPeerConnection | null = null;
    private config: WhipClientConfig;
    private connectionId: string | null = null;
    private onStateChangeCallback: WhipConnectionStateCallback | null = null;
    private onErrorCallback: WhipErrorCallback | null = null;
    private currentStream: MediaStream | null = null;
    private reconnectAttempts = 0;
    private reconnectTimeout: number | null = null;
    private isReconnecting = false;
    private maxReconnectAttempts: number;
    private enableAutoReconnect: boolean;

    constructor(config: WhipClientConfig) {
        this.config = config;
        this.enableAutoReconnect = config.enableAutoReconnect ?? true;
        this.maxReconnectAttempts = config.maxReconnectAttempts ?? 3;
    }

    /**
     * Set callback for connection state changes
     */
    onStateChange(callback: WhipConnectionStateCallback): void {
        this.onStateChangeCallback = callback;
    }

    /**
     * Set callback for errors
     */
    onError(callback: WhipErrorCallback): void {
        this.onErrorCallback = callback;
    }

    /**
     * Connect audio stream to backend using WHIP protocol
     */
    async connect(audioStream: MediaStream): Promise<void> {
        try {
            // Log stream details for debugging
            console.log('WHIP Client connecting with stream:', {
                streamId: audioStream.id,
                trackCount: audioStream.getTracks().length,
                tracks: audioStream.getTracks().map(t => ({
                    kind: t.kind,
                    id: t.id,
                    label: t.label,
                    enabled: t.enabled,
                    readyState: t.readyState
                }))
            });

            // Store stream for reconnection
            this.currentStream = audioStream;
            this.reconnectAttempts = 0;
            this.isReconnecting = false;

            await this.establishConnection(audioStream);

        } catch (error) {
            // Clean up on error
            this.disconnect();
            throw error;
        }
    }

    /**
     * Establish WebRTC connection
     */
    private async establishConnection(audioStream: MediaStream): Promise<void> {
        // Create RTCPeerConnection with ICE servers
        // Note: Cloudflare Realtime Kit uses ice-lite, so we only need STUN servers
        this.peerConnection = new RTCPeerConnection({
            iceServers: [
                { urls: 'stun:stun.cloudflare.com:3478' },
            ],
            bundlePolicy: 'max-bundle',
            rtcpMuxPolicy: 'require',
            iceCandidatePoolSize: 10,
        });

        // Set up connection state listeners
        this.setupConnectionListeners();

        // Add audio tracks to peer connection
        const tracks = audioStream.getTracks();
        console.log(`Adding ${tracks.length} track(s) to peer connection:`, tracks.map(t => ({
            kind: t.kind,
            id: t.id,
            enabled: t.enabled,
            readyState: t.readyState
        })));

        tracks.forEach(track => {
            if (this.peerConnection) {
                const sender = this.peerConnection.addTrack(track, audioStream);
                console.log('Track added to peer connection:', {
                    trackId: track.id,
                    senderId: sender.track?.id
                });
            }
        });

        // Create SDP offer
        const offer = await this.peerConnection.createOffer({
            offerToReceiveAudio: false,
            offerToReceiveVideo: false,
        });

        // Set local description
        await this.peerConnection.setLocalDescription(offer);

        // Wait for ICE gathering to complete
        await this.waitForIceGathering();

        // Get the complete SDP offer with ICE candidates
        const completeSdp = this.peerConnection.localDescription?.sdp;
        if (!completeSdp) {
            throw new Error('Failed to generate SDP offer');
        }

        // Send WHIP request to backend
        const sdpAnswer = await this.sendWhipRequest(completeSdp);

        // Set remote description with SDP answer
        await this.peerConnection.setRemoteDescription({
            type: 'answer',
            sdp: sdpAnswer,
        });
    }

    /**
     * Wait for ICE gathering to complete
     */
    private waitForIceGathering(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.peerConnection) {
                reject(new Error('No peer connection'));
                return;
            }

            // If already complete, resolve immediately
            if (this.peerConnection.iceGatheringState === 'complete') {
                resolve();
                return;
            }

            // Set timeout for ICE gathering (10 seconds)
            const timeout = setTimeout(() => {
                reject(new Error('ICE gathering timeout'));
            }, 10000);

            // Listen for ICE gathering state changes
            const handleIceGatheringStateChange = () => {
                if (this.peerConnection?.iceGatheringState === 'complete') {
                    clearTimeout(timeout);
                    this.peerConnection.removeEventListener(
                        'icegatheringstatechange',
                        handleIceGatheringStateChange
                    );
                    resolve();
                }
            };

            this.peerConnection.addEventListener(
                'icegatheringstatechange',
                handleIceGatheringStateChange
            );
        });
    }

    /**
     * Send WHIP request to backend
     */
    private async sendWhipRequest(sdpOffer: string): Promise<string> {
        const whipUrl = `${this.config.apiUrl}/rtc/sessions/${this.config.sessionId}/whip`;

        const response = await fetch(whipUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/sdp',
                'Authorization': `Bearer ${this.config.authToken}`,
            },
            body: sdpOffer,
        });

        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = 'Failed to establish WHIP connection';

            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.error?.message || errorMessage;
            } catch {
                // If not JSON, use status text
                errorMessage = `${errorMessage}: ${response.statusText}`;
            }

            throw new Error(errorMessage);
        }

        // Extract connection ID from Location header
        const locationHeader = response.headers.get('Location');
        if (locationHeader) {
            const match = locationHeader.match(/\/whip\/([^/]+)$/);
            if (match) {
                this.connectionId = match[1];
            }
        }

        // Get SDP answer from response body
        const sdpAnswer = await response.text();
        if (!sdpAnswer || !sdpAnswer.includes('v=0')) {
            throw new Error('Invalid SDP answer received from server');
        }

        return sdpAnswer;
    }

    /**
     * Set up connection state listeners
     */
    private setupConnectionListeners(): void {
        if (!this.peerConnection) return;

        // Connection state change
        this.peerConnection.addEventListener('connectionstatechange', () => {
            this.notifyStateChange();

            // Handle connection failures
            if (this.peerConnection?.connectionState === 'failed') {
                console.error('WebRTC connection failed');
                this.handleConnectionFailure();
            }

            // Note: 'disconnected' state is temporary and can recover
            // Only treat 'failed' as a permanent failure
            if (this.peerConnection?.connectionState === 'disconnected') {
                console.warn('WebRTC connection temporarily disconnected, waiting for recovery...');
            }
        });

        // ICE connection state change
        this.peerConnection.addEventListener('iceconnectionstatechange', () => {
            this.notifyStateChange();

            if (this.peerConnection?.iceConnectionState === 'failed') {
                console.error('ICE connection failed');
                this.handleConnectionFailure();
            }

            if (this.peerConnection?.iceConnectionState === 'disconnected') {
                console.warn('ICE connection disconnected');
                // Don't trigger reconnection on ICE disconnected, wait for connection state
            }
        });

        // ICE gathering state change
        this.peerConnection.addEventListener('icegatheringstatechange', () => {
            this.notifyStateChange();
        });

        // ICE candidate events (for debugging)
        this.peerConnection.addEventListener('icecandidate', (event) => {
            if (event.candidate) {
                console.debug('ICE candidate:', event.candidate.candidate);
            } else {
                console.debug('ICE gathering complete');
            }
        });

        // Track events (for debugging)
        this.peerConnection.addEventListener('track', (event) => {
            console.debug('Track event:', event.track.kind, event.streams);
        });
    }

    /**
     * Notify state change callback
     */
    private notifyStateChange(): void {
        if (this.onStateChangeCallback && this.peerConnection) {
            this.onStateChangeCallback({
                state: this.peerConnection.connectionState,
                iceConnectionState: this.peerConnection.iceConnectionState,
                iceGatheringState: this.peerConnection.iceGatheringState,
                reconnecting: this.isReconnecting,
                reconnectAttempt: this.reconnectAttempts,
            });
        }
    }

    /**
     * Handle connection failure and attempt reconnection
     */
    private handleConnectionFailure(): void {
        // Don't attempt reconnection if already reconnecting or disabled
        if (this.isReconnecting || !this.enableAutoReconnect) {
            console.warn('Connection failed. Auto-reconnect is disabled.');
            if (this.onErrorCallback) {
                this.onErrorCallback(new Error('Connection lost.'));
            }
            return;
        }

        // Don't reconnect if we've exceeded max attempts
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached');
            if (this.onErrorCallback) {
                this.onErrorCallback(new Error('Connection lost. Maximum reconnection attempts reached.'));
            }
            return;
        }

        // Attempt reconnection with exponential backoff
        this.attemptReconnection();
    }

    /**
     * Attempt to reconnect with exponential backoff
     */
    private async attemptReconnection(): Promise<void> {
        if (!this.currentStream || this.isReconnecting) {
            return;
        }

        this.isReconnecting = true;
        this.reconnectAttempts++;

        // Calculate backoff delay: 1s, 2s, 4s
        const backoffDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 4000);

        console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${backoffDelay}ms`);

        // Notify state change
        this.notifyStateChange();

        // Wait for backoff delay
        await new Promise(resolve => {
            this.reconnectTimeout = window.setTimeout(resolve, backoffDelay);
        });

        try {
            // Close existing connection
            if (this.peerConnection) {
                this.peerConnection.close();
                this.peerConnection = null;
            }

            // Establish new connection
            await this.establishConnection(this.currentStream);

            // Reset reconnection state on success
            this.isReconnecting = false;
            this.reconnectAttempts = 0;

            console.log('Reconnection successful');

        } catch (error) {
            console.error('Reconnection failed:', error);
            this.isReconnecting = false;

            // Try again if we haven't exceeded max attempts
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.handleConnectionFailure();
            } else {
                if (this.onErrorCallback) {
                    this.onErrorCallback(new Error('Failed to reconnect after multiple attempts'));
                }
            }
        }
    }

    /**
     * Manually trigger reconnection
     */
    async reconnect(): Promise<void> {
        if (!this.currentStream) {
            throw new Error('No stream available for reconnection');
        }

        // Reset reconnection state
        this.reconnectAttempts = 0;
        this.isReconnecting = false;

        // Close existing connection
        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }

        // Establish new connection
        await this.establishConnection(this.currentStream);
    }

    /**
     * Get current connection state
     */
    getConnectionState(): WhipConnectionState | null {
        if (!this.peerConnection) return null;

        return {
            state: this.peerConnection.connectionState,
            iceConnectionState: this.peerConnection.iceConnectionState,
            iceGatheringState: this.peerConnection.iceGatheringState,
        };
    }

    /**
     * Get connection ID
     */
    getConnectionId(): string | null {
        return this.connectionId;
    }

    /**
     * Check if connected
     */
    isConnected(): boolean {
        return this.peerConnection?.connectionState === 'connected';
    }

    /**
     * Disconnect and clean up resources
     */
    disconnect(): void {
        // Clear reconnection timeout
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        // Reset reconnection state
        this.isReconnecting = false;
        this.reconnectAttempts = 0;

        if (this.peerConnection) {
            // Close peer connection
            this.peerConnection.close();
            this.peerConnection = null;
        }

        this.connectionId = null;
        this.currentStream = null;
    }

    /**
     * Get peer connection statistics
     */
    async getStats(): Promise<RTCStatsReport | null> {
        if (!this.peerConnection) return null;
        return await this.peerConnection.getStats();
    }
}
